/**
 * Employee Qualifications Tab Component
 * 
 * This component is extracted from EmployeeForm.jsx for modularity.
 * It maintains the exact same structure and data-testid attributes.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { Award, Plus, Trash2, ExternalLink, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateDE } from './helpers';
import { openAuthFile } from '../../lib/openAuthFile';
import { FileDropzone } from '../../components/ui/file-dropzone';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import { ExpiryBadge } from '../profile/profileHelpers';

const QualificationsTab = ({
  formData,
  setFormData,
  newQualification,
  setNewQualification,
  addQualification,
  removeQualification,
  handleQualificationFileUpload,
  uploadingQualification,
}) => {
  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Award className="h-5 w-5 text-primary" />
          Qualifikationen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Existing Qualifications */}
        {formData.qualifications?.length > 0 && (
          <div className="space-y-3">
            <Label>Vorhandene Qualifikationen</Label>
            {formData.qualifications.map((qual, index) => {
              // Handle both old string format and new object format
              const isObject = typeof qual === 'object';
              const qualName = isObject ? qual.name : qual;
              const qualId = isObject ? qual.id : `legacy-${index}`;
              const docUrl = isObject ? qual.document_url : null;
              const docFilename = isObject ? qual.document_filename : null;
              const qualDate = isObject ? qual.date : null;
              const qualExpiry = isObject ? qual.expiry_date : null;
              
              return (
                <div
                  key={qualId}
                  className="flex items-start justify-between p-4 rounded-lg bg-secondary/30"
                  data-testid={`qualification-${qualId}`}
                >
                  <div className="space-y-1">
                    <p className="font-medium">{qualName}</p>
                    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                      {qualDate && <span>Erworben: {formatDateDE(qualDate)}</span>}
                      {qualExpiry && (
                        <ExpiryBadge expiry={qualExpiry} />
                      )}
                    </div>
                    {docUrl ? (
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            openAuthFile(docUrl, { filename: docFilename })
                              .catch((err) => toast.error(`Anzeigen fehlgeschlagen: ${err.message || err}`))
                          }
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                          data-testid={`open-qualification-doc-${qualId}`}
                        >
                          <ExternalLink className="h-3 w-3" />
                          {docFilename || 'Nachweis anzeigen'}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            openAuthFile(docUrl, { filename: docFilename || 'nachweis.pdf', forceDownload: true })
                              .catch((err) => toast.error(`Download fehlgeschlagen: ${err.message || err}`))
                          }
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
                          data-testid={`download-qualification-doc-${qualId}`}
                        >
                          Herunterladen
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Kein Nachweis hinterlegt</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        // Edit: Load into form
                        setNewQualification({
                          name: qualName,
                          date: qualDate || '',
                          expiry_date: qualExpiry || '',
                          document_url: docUrl || '',
                          document_filename: docFilename || '',
                          editing_id: qualId,
                        });
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDeleteDialog
                      trigger={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          data-testid={`remove-qualification-${qualId}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      }
                      title="Qualifikation löschen?"
                      description={`Möchten Sie die Qualifikation „${qualName}“ wirklich entfernen? ${docUrl ? 'Das hinterlegte Nachweis-Dokument wird nicht mehr verlinkt sein.' : ''}`}
                      onConfirm={() => isObject ? removeQualification(qualId) : setFormData(prev => ({
                        ...prev,
                        qualifications: prev.qualifications.filter((_, i) => i !== index)
                      }))}
                      testIdPrefix={`remove-qualification-${qualId}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit Qualification Form */}
        <div className="space-y-4 p-4 rounded-lg border border-dashed border-border">
          <Label className="text-base font-medium">
            {newQualification.editing_id ? 'Qualifikation bearbeiten' : 'Neue Qualifikation hinzufügen'}
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="qual_name">Bezeichnung *</Label>
              <Input
                id="qual_name"
                value={newQualification.name || ''}
                onChange={(e) => setNewQualification((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="z.B. Abschluss"
                data-testid="new-qualification-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="qual_date">Erworben am</Label>
              <DatePickerDE
                value={newQualification.date || ''}
                onChange={(v) => setNewQualification((prev) => ({ ...prev, date: v }))}
                placeholder="Erwerbsdatum"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="qual_expiry">Gültig bis (optional)</Label>
              <DatePickerDE
                value={newQualification.expiry_date || ''}
                onChange={(v) => setNewQualification((prev) => ({ ...prev, expiry_date: v }))}
                placeholder="Ablaufdatum"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Nachweis-Dokument</Label>
              <FileDropzone
                accept=".pdf,.jpg,.jpeg,.png"
                onUpload={handleQualificationFileUpload}
                uploading={uploadingQualification}
                filename={newQualification.document_filename}
                testId="qualification-file-dropzone"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              type="button" 
              onClick={addQualification} 
              disabled={!newQualification.name.trim()}
              data-testid="add-qualification-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              {newQualification.editing_id ? 'Änderungen speichern' : 'Qualifikation hinzufügen'}
            </Button>
            {newQualification.editing_id && (
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setNewQualification({ name: '', date: '', expiry_date: '', document_url: '', document_filename: '', editing_id: null })}
              >
                Abbrechen
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default QualificationsTab;
